import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Calendar, Image, ClipboardList, Flag, Upload, Trash2, Send, Pencil, Users, UserMinus } from "lucide-react";

export default function CollectionPlanning() {
  const { user } = useAuth();
  const [seasons, setSeasons] = useState([]);
  const [collections, setCollections] = useState([]);
  const [linePlans, setLinePlans] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("seasons");
  const [showCreate, setShowCreate] = useState(false);
  const [showCollectionCreate, setShowCollectionCreate] = useState(false);
  const [showMoodBoard, setShowMoodBoard] = useState(null);
  const [moodBoardImages, setMoodBoardImages] = useState([]);
  const [form, setForm] = useState({ season_code: "" });
  const [collForm, setCollForm] = useState({ name: "", brand: "", season: "", year: "", theme_statement: "", target_consumer_profile: "" });
  const [saving, setSaving] = useState(false);
  const [editCollection, setEditCollection] = useState(null);
  const [editCollForm, setEditCollForm] = useState({ name: "", brand: "", season: "", year: "", theme_statement: "", target_consumer_profile: "" });

  // Calendar/milestone state
  const [showCreateCalendar, setShowCreateCalendar] = useState(false);
  const [calendarCollId, setCalendarCollId] = useState("");
  const [calendars, setCalendars] = useState({});
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [milestoneCalId, setMilestoneCalId] = useState("");
  const [milestoneForm, setMilestoneForm] = useState({ name: "", planned_date: "" });
  const [editMilestone, setEditMilestone] = useState(null);
  const [editMilestoneForm, setEditMilestoneForm] = useState({ status: "", planned_date: "" });

  // Team member state
  const [showTeamPanel, setShowTeamPanel] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [addMemberId, setAddMemberId] = useState("");
  const [filterMine, setFilterMine] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/season-codes").then((r) => setSeasons(r.data)).catch(() => {}),
      api.get("/collections").then((r) => setCollections(r.data)).catch(() => {}),
      api.get("/line-plans").then((r) => setLinePlans(r.data)).catch(() => {}),
      api.get("/calendar/milestones").then((r) => setMilestones(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/season-codes", { code: form.season_code });
      toast.success("Season created");
      setShowCreate(false);
      setForm({ season_code: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (plan) => {
    const next = !plan.active;
    try {
      await api.patch(`/season-codes/${plan.id}`, { active: next });
      toast.success(next ? "Season activated" : "Season deactivated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createCollection = async () => {
    setSaving(true);
    try {
      await api.post("/collections", {
        name: collForm.name,
        brand: collForm.brand,
        season_code: collForm.season,
        year: parseInt(collForm.year) || new Date().getFullYear(),
        theme_statement: collForm.theme_statement || undefined,
        target_consumer_profile: collForm.target_consumer_profile || undefined,
      });
      toast.success("Collection created");
      setShowCollectionCreate(false);
      setCollForm({ name: "", brand: "", season: "", year: "", theme_statement: "", target_consumer_profile: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const publishCollection = async (collectionId) => {
    try {
      await api.patch(`/collections/${collectionId}/publish`);
      toast.success("Collection published");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateCollection = async () => {
    setSaving(true);
    try {
      await api.patch(`/collections/${editCollection}`, {
        name: editCollForm.name,
        brand: editCollForm.brand,
        season_code: editCollForm.season,
        year: parseInt(editCollForm.year) || undefined,
        theme_statement: editCollForm.theme_statement || undefined,
        target_consumer_profile: editCollForm.target_consumer_profile || undefined,
      });
      toast.success("Collection updated");
      setEditCollection(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const startEditCollection = (c) => {
    setEditCollection(c.id);
    setEditCollForm({ name: c.name || "", brand: c.brand || "", season: c.season_code || c.season || "", year: c.year || "", theme_statement: c.theme_statement || "", target_consumer_profile: c.target_consumer_profile || "" });
  };

  const openMoodBoard = async (collectionId) => {
    try {
      const r = await api.get(`/collections/${collectionId}/mood-board`);
      setMoodBoardImages(r.data || []);
    } catch {
      setMoodBoardImages([]);
    }
    setShowMoodBoard(collectionId);
  };

  const uploadMoodBoardImage = async (collectionId, file) => {
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post(`/collections/${collectionId}/mood-board`, {
            image_url: reader.result,
            annotation: file.name,
          });
          toast.success("Image added to mood board");
          openMoodBoard(collectionId);
        } catch (e) {
          toast.error(apiError(e));
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteMoodBoardImage = async (imageId) => {
    try {
      await api.delete(`/mood-board-images/${imageId}`);
      toast.success("Image removed");
      if (showMoodBoard) openMoodBoard(showMoodBoard);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Load calendars for collections (to get calendar IDs for milestone creation)
  const loadCalendarsForCollections = async () => {
    const cals = {};
    for (const c of collections) {
      try {
        const r = await api.get(`/collections/${c.id}/calendar`);
        if (r.data && r.data.id) cals[c.id] = r.data;
      } catch { /* no calendar yet */ }
    }
    setCalendars(cals);
  };

  const createCalendar = async () => {
    if (!calendarCollId) return;
    setSaving(true);
    try {
      await api.post(`/collections/${calendarCollId}/calendar`);
      toast.success("Calendar created");
      setShowCreateCalendar(false);
      setCalendarCollId("");
      load();
      loadCalendarsForCollections();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addMilestone = async () => {
    if (!milestoneCalId || !milestoneForm.name) return;
    setSaving(true);
    try {
      await api.post(`/calendars/${milestoneCalId}/milestones`, {
        name: milestoneForm.name,
        planned_date: milestoneForm.planned_date || null,
      });
      toast.success("Milestone added");
      setShowAddMilestone(false);
      setMilestoneForm({ name: "", planned_date: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const updateMilestoneAction = async () => {
    if (!editMilestone) return;
    setSaving(true);
    try {
      const payload = {};
      if (editMilestoneForm.status) payload.status = editMilestoneForm.status;
      if (editMilestoneForm.planned_date) payload.planned_date = editMilestoneForm.planned_date;
      await api.patch(`/milestones/${editMilestone}`, payload);
      toast.success("Milestone updated");
      setEditMilestone(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Load calendars when switching to milestones tab
  useEffect(() => {
    if (tab === "milestones" && collections.length > 0) loadCalendarsForCollections();
  }, [tab, collections.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Team member management
  const openTeamPanel = async (collectionId) => {
    setShowTeamPanel(collectionId);
    try {
      const [membersRes, usersRes] = await Promise.all([
        api.get(`/collections/${collectionId}/team-members`),
        api.get("/users"),
      ]);
      setTeamMembers(membersRes.data || []);
      setAllUsers(usersRes.data || []);
    } catch {
      setTeamMembers([]);
      setAllUsers([]);
    }
  };

  const addTeamMember = async () => {
    if (!showTeamPanel || !addMemberId) return;
    try {
      await api.post(`/collections/${showTeamPanel}/team-members`, { user_id: addMemberId });
      toast.success("Team member added");
      setAddMemberId("");
      openTeamPanel(showTeamPanel);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeTeamMember = async (memberId) => {
    if (!showTeamPanel) return;
    try {
      await api.delete(`/collections/${showTeamPanel}/team-members/${memberId}`);
      toast.success("Team member removed");
      openTeamPanel(showTeamPanel);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const displayedCollections = filterMine
    ? collections.filter((c) =>
        c.owner_id === user?.id ||
        (c.team_member_ids && c.team_member_ids.includes(user?.id))
      )
    : collections;

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Collection Planning</h1>
          <p className="text-zinc-500 mt-2 text-sm">Manage season codes, line plans and milestone calendars.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New season
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        <button onClick={() => setTab("seasons")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "seasons" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Season Codes
        </button>
        <button onClick={() => setTab("lineplans")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "lineplans" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Line Plans
        </button>
        <button onClick={() => setTab("milestones")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === "milestones" ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
          Milestones
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "seasons" ? (
        seasons.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <Calendar size={26} className="mx-auto mb-3" />
            <p className="text-sm">No season codes yet.</p>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {seasons.map((s) => (
                <div key={s.id} className="border border-zinc-200 bg-white rounded-lg overflow-hidden hover:shadow-sm transition-shadow">
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{s.code}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                        {s.active ? "active" : "inactive"}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">Created {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</p>
                    <p className="text-xs text-zinc-500 mt-2">{collections.filter((c) => (c.season_code || c.season) === s.code).length} collection(s)</p>
                    <div className="flex items-center gap-2 mt-3">
                      <button onClick={() => toggleStatus(s)}
                        className="text-xs text-zinc-500 hover:text-zinc-800 ml-auto">
                        {s.active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {collections.length > 0 && (
              <div className="mt-10">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Collections</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setFilterMine(!filterMine)}
                      className={`text-xs px-3 py-1.5 rounded-md ${filterMine ? "bg-zinc-950 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                      My collections
                    </button>
                    <button onClick={() => setShowCollectionCreate(true)} className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 transition-colors">
                      <Plus size={12} /> New collection
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {displayedCollections.map((c) => (
                    <div key={c.id} className="border border-zinc-200 bg-white rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-zinc-400 mt-1">{c.brand} · {c.season_code || c.season} {c.year}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                          {c.status || "draft"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => openMoodBoard(c.id)}
                          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
                          <Image size={12} /> Mood board
                        </button>
                        <button onClick={() => openTeamPanel(c.id)}
                          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
                          <Users size={12} /> Team ({(c.team_member_ids || []).length})
                        </button>
                        <button onClick={() => startEditCollection(c)}
                          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
                          <Pencil size={12} /> Edit
                        </button>
                        {(!c.status || c.status === "draft") && (
                          <button onClick={() => publishCollection(c.id)}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900">
                            <Send size={12} /> Publish
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )
      ) : tab === "lineplans" ? (
        linePlans.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
            <ClipboardList size={26} className="mx-auto mb-3" />
            <p className="text-sm">No line plans yet.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {linePlans.map((lp) => (
              <div key={lp.id} className="border border-zinc-200 bg-white rounded-lg px-6 py-4 hover:shadow-sm transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center">
                    <ClipboardList size={14} className="text-zinc-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{lp.name || "Untitled Collection"}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {lp.season_code && <span className="mr-3">Season: {lp.season_code}</span>}
                      Version {lp.version}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${lp.active ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                    {lp.active ? "active" : "inactive"}
                  </span>
                  <p className="text-xs text-zinc-400">{lp.created_at ? new Date(lp.created_at).toLocaleDateString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <div className="mt-6 flex items-center gap-3">
            <button onClick={() => setShowCreateCalendar(true)}
              className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 transition-colors">
              <Calendar size={12} /> Create calendar
            </button>
            <button onClick={() => setShowAddMilestone(true)}
              className="inline-flex items-center gap-1.5 bg-zinc-950 text-white text-xs px-3 py-1.5 rounded-md hover:bg-zinc-800 transition-colors">
              <Plus size={12} /> Add milestone
            </button>
          </div>
          {milestones.length === 0 ? (
            <div className="mt-6 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <Flag size={26} className="mx-auto mb-3" />
              <p className="text-sm">No milestones yet. Create a calendar for a collection first.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {milestones.map((m) => (
                <div key={m.id} className="border border-zinc-200 bg-white rounded-lg px-6 py-4 hover:shadow-sm transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center">
                      <Flag size={14} className="text-zinc-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{m.name}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {m.collection_name && <span className="mr-3">Collection: {m.collection_name}</span>}
                        Planned: {m.planned_date ? new Date(m.planned_date).toLocaleDateString() : "Not set"}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      m.status === "completed" ? "bg-emerald-100 text-emerald-800" :
                      m.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                      "bg-zinc-100 text-zinc-700"
                    }`}>
                      {m.status}
                    </span>
                    <button onClick={() => { setEditMilestone(m.id); setEditMilestoneForm({ status: m.status || "pending", planned_date: m.planned_date || "" }); }}
                      className="p-1.5 hover:bg-zinc-100 rounded-md text-zinc-500 hover:text-zinc-900">
                      <Pencil size={14} />
                    </button>
                    {m.completed_at && <p className="text-xs text-zinc-400">Done {new Date(m.completed_at).toLocaleDateString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New season</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.season_code} onChange={(e) => setForm({ ...form, season_code: e.target.value })} placeholder="Season code (e.g. SS26)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <button onClick={create} disabled={!form.season_code || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create season"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCollectionCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCollectionCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New collection</h2>
              <button onClick={() => setShowCollectionCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={collForm.name} onChange={(e) => setCollForm({ ...collForm, name: e.target.value })} placeholder="Collection name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={collForm.brand} onChange={(e) => setCollForm({ ...collForm, brand: e.target.value })} placeholder="Brand"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={collForm.season} onChange={(e) => setCollForm({ ...collForm, season: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select season</option>
                {seasons.filter((s) => s.active).map((s) => (
                  <option key={s.id} value={s.code}>{s.code}</option>
                ))}
              </select>
              <input value={collForm.year} onChange={(e) => setCollForm({ ...collForm, year: e.target.value })} placeholder="Year" type="number"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <textarea value={collForm.theme_statement} onChange={(e) => setCollForm({ ...collForm, theme_statement: e.target.value })} placeholder="Theme statement"
                  rows={3} className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                <p className={`text-[11px] mt-1 ${(collForm.theme_statement || "").length >= 50 ? "text-zinc-400" : "text-amber-600"}`}>
                  {(collForm.theme_statement || "").length}/50 characters minimum for publishing
                </p>
              </div>
              <textarea value={collForm.target_consumer_profile} onChange={(e) => setCollForm({ ...collForm, target_consumer_profile: e.target.value })} placeholder="Target consumer profile"
                rows={2} className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button onClick={createCollection} disabled={!collForm.name || !collForm.brand || !collForm.season || !collForm.year || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create collection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editCollection && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setEditCollection(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Edit collection</h2>
              <button onClick={() => setEditCollection(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={editCollForm.name} onChange={(e) => setEditCollForm({ ...editCollForm, name: e.target.value })} placeholder="Collection name"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input value={editCollForm.brand} onChange={(e) => setEditCollForm({ ...editCollForm, brand: e.target.value })} placeholder="Brand"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <select value={editCollForm.season} onChange={(e) => setEditCollForm({ ...editCollForm, season: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select season</option>
                {seasons.filter((s) => s.active).map((s) => (
                  <option key={s.id} value={s.code}>{s.code}</option>
                ))}
              </select>
              <input value={editCollForm.year} onChange={(e) => setEditCollForm({ ...editCollForm, year: e.target.value })} placeholder="Year" type="number"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <textarea value={editCollForm.theme_statement} onChange={(e) => setEditCollForm({ ...editCollForm, theme_statement: e.target.value })} placeholder="Theme statement"
                  rows={3} className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
                <p className={`text-[11px] mt-1 ${(editCollForm.theme_statement || "").length >= 50 ? "text-zinc-400" : "text-amber-600"}`}>
                  {(editCollForm.theme_statement || "").length}/50 characters minimum for publishing
                </p>
              </div>
              <textarea value={editCollForm.target_consumer_profile} onChange={(e) => setEditCollForm({ ...editCollForm, target_consumer_profile: e.target.value })} placeholder="Target consumer profile"
                rows={2} className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button onClick={updateCollection} disabled={!editCollForm.name || !editCollForm.brand || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Calendar Modal */}
      {showCreateCalendar && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreateCalendar(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Create calendar</h2>
              <button onClick={() => setShowCreateCalendar(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={calendarCollId} onChange={(e) => setCalendarCollId(e.target.value)}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a collection</option>
                {collections.filter((c) => !calendars[c.id]).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button onClick={createCalendar} disabled={!calendarCollId || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create calendar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Milestone Modal */}
      {showAddMilestone && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowAddMilestone(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Add milestone</h2>
              <button onClick={() => setShowAddMilestone(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={milestoneCalId} onChange={(e) => setMilestoneCalId(e.target.value)}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a calendar</option>
                {Object.entries(calendars).map(([collId, cal]) => {
                  const coll = collections.find((c) => c.id === collId);
                  return <option key={cal.id} value={cal.id}>{coll?.name || "Collection"} calendar</option>;
                })}
              </select>
              <input value={milestoneForm.name} onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })} placeholder="Milestone name (e.g. Proto review)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-[10px] text-zinc-500 mb-1 block">Planned date</label>
                <input type="date" value={milestoneForm.planned_date} onChange={(e) => setMilestoneForm({ ...milestoneForm, planned_date: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={addMilestone} disabled={!milestoneCalId || !milestoneForm.name || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Adding..." : "Add milestone"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Milestone Modal */}
      {editMilestone && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setEditMilestone(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Update milestone</h2>
              <button onClick={() => setEditMilestone(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <select value={editMilestoneForm.status} onChange={(e) => setEditMilestoneForm({ ...editMilestoneForm, status: e.target.value })}
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <div>
                <label className="text-[10px] text-zinc-500 mb-1 block">Planned date</label>
                <input type="date" value={editMilestoneForm.planned_date ? editMilestoneForm.planned_date.slice(0, 10) : ""} onChange={(e) => setEditMilestoneForm({ ...editMilestoneForm, planned_date: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={updateMilestoneAction} disabled={saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Members Modal */}
      {showTeamPanel && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowTeamPanel(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Team Members</h2>
              <button onClick={() => setShowTeamPanel(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            {teamMembers.length > 0 ? (
              <div className="space-y-2 mb-4">
                {teamMembers.map((m) => (
                  <div key={m.id} className="flex items-center justify-between bg-zinc-50 px-3 py-2 rounded-md">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-xs text-zinc-400">{m.email}</p>
                    </div>
                    <button onClick={() => removeTeamMember(m.id)} className="p-1.5 hover:bg-red-50 rounded-md text-red-400" title="Remove">
                      <UserMinus size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 mb-4">No team members assigned yet.</p>
            )}
            <div className="flex gap-2">
              <select value={addMemberId} onChange={(e) => setAddMemberId(e.target.value)}
                className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                <option value="">Select a user</option>
                {allUsers.filter((u) => !teamMembers.some((m) => m.id === (u.id || u.user_id))).map((u) => (
                  <option key={u.id || u.user_id} value={u.id || u.user_id}>{u.name} ({u.email})</option>
                ))}
              </select>
              <button onClick={addTeamMember} disabled={!addMemberId}
                className="bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showMoodBoard && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowMoodBoard(null)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">Mood Board</h2>
              <button onClick={() => setShowMoodBoard(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="mb-4">
              <label className="inline-flex items-center gap-2 text-xs border border-zinc-300 rounded-md px-3 py-1.5 hover:bg-zinc-100 transition-colors cursor-pointer">
                <Upload size={12} /> Upload image
                <input type="file" className="hidden" accept="image/*"
                  onChange={(e) => { if (e.target.files[0]) uploadMoodBoardImage(showMoodBoard, e.target.files[0]); }} />
              </label>
            </div>
            {moodBoardImages.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-lg p-12 text-center text-zinc-400">
                <Image size={24} className="mx-auto mb-2" />
                <p className="text-sm">No mood board images yet. Upload to get started.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {moodBoardImages.map((img) => (
                  <div key={img.id} className="relative group">
                    <img src={img.url || img.image_url} alt={img.caption || ""} className="w-full h-32 object-cover rounded-md" />
                    <button onClick={() => deleteMoodBoardImage(img.id)}
                      className="absolute top-1 right-1 bg-white/80 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={12} className="text-red-600" />
                    </button>
                    {img.caption && <p className="text-xs text-zinc-500 mt-1">{img.caption}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
